import { IconType } from '@factorialco/f0-react'
import {
  BulkAssignJobcatalogNodeAttributeAttributesInput,
  JobCatalogCompetencyAttributeInput,
  JobCatalogEnumsJobCatalogTreeAttributeTypesEnum,
} from 'generated/resources/datatable.graphql'
import {
  NodeCompetenciesDataCollectionCompetencyAttributeFragment,
  NodeCompetenciesDataCollectionCompetencyLevelAttributeFragment,
  NodeCompetenciesDataCollectionNodeAttributeFragment,
  NodeCompetenciesPageRoleLevelNodeAttributeFragment,
  NodeCompetenciesPageRoleLevelNodeFragment,
} from 'generated/resources/graphql'

import { type Competency as CompetenciesFinderCompetency } from 'modules/competencies/components/CompetenciesFinder'

export interface CompetencyDataCollectionItem
  extends NodeCompetenciesDataCollectionCompetencyAttributeFragment {
  id: number
  nodeAttributeId: number
  definedAt: string
  icon: IconType
  readonly: boolean
}

interface CompetencyLevelFlattened
  extends NodeCompetenciesDataCollectionCompetencyLevelAttributeFragment {
  competencyId: number
  nodeUuid: string
}

const COMPETENCY_ATTRIBUTE_TYPE: JobCatalogEnumsJobCatalogTreeAttributeTypesEnum = 'competency'

export const isCompetencyAttribute = (
  attr:
    | NodeCompetenciesPageRoleLevelNodeAttributeFragment
    | NodeCompetenciesDataCollectionNodeAttributeFragment
): boolean => {
  if (attr.type !== COMPETENCY_ATTRIBUTE_TYPE) return false

  return Boolean(attr.attributeId && attr.takesPrecedence && attr.valueCompetency)
}

const getCompetencyLevelsFromAttributes = (
  uuid: string,
  attributes: NodeCompetenciesPageRoleLevelNodeAttributeFragment[]
): CompetencyLevelFlattened[] => {
  return attributes
    .filter(isCompetencyAttribute)
    .map(({ attributeId, valueCompetency }): CompetencyLevelFlattened | null => {
      const { level } = valueCompetency || {}
      if (!level) return null

      return { ...level, competencyId: Number(attributeId), nodeUuid: uuid }
    })
    .filter(Boolean)
}

export const getCompetencyLevelsFromNodes = (
  nodes: NodeCompetenciesPageRoleLevelNodeFragment[]
): CompetencyLevelFlattened[] => {
  return nodes
    .map((node) => getCompetencyLevelsFromAttributes(node.uuid, node?.attributes || []))
    .flat()
}

const buildCompetencyAttribute = (
  competency: CompetencyDataCollectionItem | CompetenciesFinderCompetency
): JobCatalogCompetencyAttributeInput => {
  const level = 'level' in competency && competency.level

  return {
    name: competency.name,
    description: competency.description,
    level: level ? { id: level.id, name: level.name, description: level.description } : undefined,
  }
}

export const buildBulkAssignCompetencyAttributes = (
  currentCompetencies: CompetencyDataCollectionItem[],
  assignedCompetencies: CompetenciesFinderCompetency[]
): BulkAssignJobcatalogNodeAttributeAttributesInput[] => {
  return assignedCompetencies.reduce<BulkAssignJobcatalogNodeAttributeAttributesInput[]>(
    (list, assigned) => {
      const current = currentCompetencies.find((c) => c.id === assigned.id)
      if (current?.readonly) return list

      return [
        ...list,
        {
          attributeId: assigned.id.toString(),
          attributeCompetency: buildCompetencyAttribute({ ...current, ...assigned }),
        },
      ]
    },
    []
  )
}
